import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MotionConfig, motion } from 'framer-motion';
import { doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuCalendarClock, LuFlag, LuFlaskConical, LuToggleRight } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
// Only global theme variables are left in this file; importing it here keeps
// its place in the cascade (other pages' styles still use them).
import './ProjectsManagementPage.module.css';
// The dialogs below are still the legacy ones (they bring their own styles)
import VisitsModal from './VisitsModal';
import WorkersModal from './WorkersModal';
import AddProjectModal from './AddProjectModal';
import EditProjectModal from './EditProjectModal';
import FileUploadModal from './FileUploadModal';
import PatientsModal from './PatientsModal';
import './ConfirmationModal.module.css';
import ProjectStatsModal from './ProjectStatsModal';
import ProjectCardModal from './ProjectCardModal';
// Global dark-mode sheet; the legacy dialogs (and other pages) rely on it
import './ProjectsManagementDarkMode.css';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import TablePagination from '../../components/data/TablePagination';
import ToggleChip from '../../components/data/ToggleChip';
import { fadeUp, staggerContainer } from '../../components/ui/motionPresets';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import { useTranslation } from '../../i18n/LanguageContext';
import ProjectCell, { ProjectCardRow, getCellTitle } from './components/ProjectCell';
import ProjectDetailPane from './components/ProjectDetailPane';
import { ProjectPageHeader, ProjectStats } from './components/ProjectOverview';
import ScreenFailModal from './components/ScreenFailModal';
import { createTestProject, downloadProjectsExcel } from './projectActions';
import {
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  PROJECT_COLUMNS,
  columnLabel,
  formatIsoDate,
  getFilterState,
  getFilterValueLabel,
  isFinishedFlag,
  optionLabel,
  projectStatusOf,
} from './projectColumns';
import useProjectDirectory from './useProjectDirectory';

const MAIN_ADMIN_EMAIL = 'turanemre1999@gmail.com';
const COLUMNS_STORAGE_KEY = 'projectsManagement.tableColumns.v1';
// From this width the project panel sits beside the table instead of over it
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
// The table card ends this far above the bottom of the screen (Layout's main padding)
const FIT_BOTTOM_GAP = 24;
// Below this the card stops shrinking and the page scrolls instead
const FIT_MIN_HEIGHT = 400;
// Below this list width the table turns into a card list with fixed pages
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

// Dialogs about the selected project (the selection is never dropped while one is open)
const PROJECT_DIALOGS = ['card', 'edit', 'workers', 'visits', 'patients', 'files', 'screenFail', 'status', 'finish'];

/**
 * Proje Yönetimi (Tailwind), built like Çalışan and Müşteri Yönetimi: the
 * table card reaches at most the bottom of the screen and pages as many rows
 * as fit; the selected project's panel is docked beside it on wide screens
 * and a drawer otherwise; narrow screens get a card list and a filter sheet.
 * The add/edit, files, staff, visits and patients dialogs are still the
 * legacy ones and render outside the animated wrapper, so its transform
 * can't trap their fixed overlays.
 */
const ProjectsManagementPage = () => {
  const { t, language, locale } = useTranslation();
  const { userEmail } = useUserProfile();
  const isMainAdmin = userEmail === MAIN_ADMIN_EMAIL;
  const {
    allProjects, filteredProjects, filters, handleFilter, clearFilters, searchTerm, setSearchTerm,
    showExpiringOnly, setShowExpiringOnly, showTestProjectsOnly, setShowTestProjectsOnly, isFilterActive,
    isLoading, hasLoaded, stats, patchProject, pageRows, pageStart, currentPage, totalPages, pageSize,
    setPageSize, goToPage, reveal, refreshProjects,
  } = useProjectDirectory(isMainAdmin);
  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: PROJECT_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  const maxCardHeight = !compactList && fitHeight ? fitHeight : undefined;

  const [selectedProject, setSelectedProject] = useState(null);
  // Open dialog: card | edit | workers | visits | patients | files | screenFail | status | finish | add | stats
  const [modal, setModal] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isCreatingTest, setIsCreatingTest] = useState(false);
  const closeModal = useCallback(() => setModal(null), []);

  const projectDialogOpen = PROJECT_DIALOGS.includes(modal);

  // The selection follows the list: refreshed data replaces it, and it is
  // dropped once the project is no longer listed (never while a dialog about
  // that project is open)
  useEffect(() => {
    if (!selectedProject || projectDialogOpen) return;
    const current = filteredProjects.find((project) => project.id === selectedProject.id);
    if (!current) setSelectedProject(null);
    else if (current !== selectedProject) setSelectedProject(current);
  }, [filteredProjects, selectedProject, projectDialogOpen]);

  // Escape clears the selection, unless a dialog, field or popover has it
  useEffect(() => {
    if (!selectedProject) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || modal || showFilterSheet) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedProject(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject, modal, showFilterSheet]);

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  const translatedOptions = useCallback((key) => (PROJECT_COLUMNS[key].options || []).map((option) => ({
    value: option.value,
    label: optionLabel(t, option),
  })), [t]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(t, key),
    filter: { type: PROJECT_COLUMNS[key].type, options: translatedOptions(key) },
  })), [columns.visibleKeys, t, translatedOptions]);

  const filterChips = Object.keys(filters)
    .filter((key) => key !== 'projectStatus' && PROJECT_COLUMNS[key] && getFilterState(key, filters[key]) !== 'off')
    .map((key) => {
      const { type } = PROJECT_COLUMNS[key];
      let value = String(filters[key]);
      if (type === 'select') value = getFilterValueLabel(t, key, filters[key]);
      else if (type === 'date') value = formatIsoDate(filters[key], locale);
      return { key, label: columnLabel(t, key), value, state: getFilterState(key, filters[key]) };
    });

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      await downloadProjectsExcel({
        isMainAdmin,
        columnKeys: columns.visibleKeys,
        messages: {
          preparing: t('projectManagement.toast.excelPreparing'),
          success: t('projectManagement.toast.excelSuccess'),
          error: t('projectManagement.toast.excelError'),
        },
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddTestProject = async () => {
    if (!isMainAdmin || isCreatingTest) return;
    setIsCreatingTest(true);
    try {
      toast.info(t('projectManagement.toast.testCreating'));
      const taskCount = await createTestProject();
      toast.success(t('projectManagement.toast.testCreated', { count: taskCount }));
      refreshProjects();
    } catch (error) {
      console.error('Error adding test project:', error);
      toast.error(t('projectManagement.toast.testError', { message: error.message }));
    } finally {
      setIsCreatingTest(false);
    }
  };

  const nextStatus = selectedProject && projectStatusOf(selectedProject) === 'Aktif' ? 'Pasif' : 'Aktif';
  const nextFinished = selectedProject ? !isFinishedFlag(selectedProject) : false;
  const projectName = selectedProject?.titleShort || t('projectManagement.confirm.projectFallback');

  const confirmStatusChange = async () => {
    setConfirmBusy(true);
    try {
      await updateDoc(doc(db, 'Projects', selectedProject.id), { projectStatus: nextStatus });
      patchProject(selectedProject.id, { projectStatus: nextStatus });
      toast.success(t('projectManagement.toast.statusUpdated', {
        status: nextStatus === 'Aktif' ? t('projectManagement.status.active') : t('projectManagement.status.inactive'),
      }));
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error(t('projectManagement.toast.statusError'));
    } finally {
      setConfirmBusy(false);
      setModal(null);
    }
  };

  // "Projeyi Bitir" only sets the isFinished flag; the project stays active
  const confirmFinish = async () => {
    setConfirmBusy(true);
    try {
      await updateDoc(doc(db, 'Projects', selectedProject.id), { isFinished: nextFinished });
      patchProject(selectedProject.id, { isFinished: nextFinished });
      toast.success(nextFinished ? t('projectManagement.toast.finished') : t('projectManagement.toast.unfinished'));
    } catch (error) {
      console.error('Error updating project finished flag:', error);
      toast.error(t('projectManagement.toast.finishError'));
    } finally {
      setConfirmBusy(false);
      setModal(null);
    }
  };

  // ↑/↓ in the panel: step through the list, turning pages as needed
  const selectedIndex = selectedProject ? filteredProjects.findIndex((project) => project.id === selectedProject.id) : -1;
  const selectSibling = (offset) => {
    const sibling = filteredProjects[selectedIndex + offset];
    if (!sibling) return;
    setSelectedProject(sibling);
    reveal(sibling.id);
  };

  const canClearFilters = Object.keys(filters).length > 0 || Boolean(searchTerm);
  const statusValue = filters.projectStatus ? String(filters.projectStatus) : '';

  const paneProps = {
    project: selectedProject,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: filteredProjects.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < filteredProjects.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedProject(null),
    onOpenCard: () => setModal('card'),
    onEdit: () => setModal('edit'),
    onOpenWorkers: () => setModal('workers'),
    onOpenVisits: () => setModal('visits'),
    onOpenPatients: () => setModal('patients'),
    onOpenFiles: () => setModal('files'),
    onOpenScreenFail: () => setModal('screenFail'),
    onToggleFinished: () => setModal('finish'),
    onToggleStatus: () => setModal('status'),
  };

  const quickFilters = (
    <>
      <ToggleChip
        pressed={showExpiringOnly}
        onChange={setShowExpiringOnly}
        icon={LuCalendarClock}
        label={t('projectManagement.toggles.dueSoon')}
        title={t('projectManagement.toggles.dueSoonHint')}
        count={hasLoaded ? stats.dueSoon : undefined}
        tone="amber"
      />
      {isMainAdmin && (
        <ToggleChip
          pressed={showTestProjectsOnly}
          onChange={setShowTestProjectsOnly}
          icon={LuFlaskConical}
          label={t('projectManagement.toggles.testOnly')}
          title={t('projectManagement.toggles.testOnlyHint')}
          tone="violet"
        />
      )}
    </>
  );

  return (
    <>
      <MotionConfig reducedMotion="user">
        <motion.div
          lang={language}
          variants={staggerContainer(0.06)}
          initial="hidden"
          animate="show"
          className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10"
        >
          <motion.div variants={fadeUp}>
            <ProjectPageHeader
              isMainAdmin={isMainAdmin}
              isExporting={isExporting}
              isCreatingTest={isCreatingTest}
              onAdd={() => setModal('add')}
              onAddTest={handleAddTestProject}
              onOpenStats={() => setModal('stats')}
              onDownload={handleDownload}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <ProjectStats total={stats.total} active={stats.active} inactive={stats.inactive} ready={hasLoaded} />
          </motion.div>

          <motion.div ref={tableAreaRef} variants={fadeUp} className="tw-flex tw-items-start tw-gap-4">
            <section ref={listCardRef} aria-label={t('projectManagement.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
              <DataToolbar
                search={{
                  value: searchTerm,
                  onChange: setSearchTerm,
                  placeholder: t('projectManagement.search.placeholder'),
                  placeholderShort: t('projectManagement.search.placeholderShort'),
                  label: t('projectManagement.search.label'),
                }}
                status={{
                  options: [
                    { value: 'Aktif', label: t('projectManagement.status.active') },
                    { value: 'Pasif', label: t('projectManagement.status.inactive') },
                    { value: '', label: t('projectManagement.status.all') },
                  ],
                  value: statusValue,
                  onChange: (value) => handleFilter('projectStatus', value),
                  layoutId: 'project-status-filter',
                }}
                columnMenu={{
                  columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(t, key) })),
                  visibleColumns: columns.visibleColumns,
                  onToggle: columns.toggleColumn,
                  onShowAll: columns.showAllColumns,
                  onReset: columns.resetColumns,
                }}
                compact={compactList}
                onOpenFilters={() => setShowFilterSheet(true)}
                chips={filterChips}
                onRemoveChip={(key) => handleFilter(key, '')}
                onClearAll={clearFilters}
                extra={quickFilters}
              />

              {compactList ? (
                <DataCardList
                  label={t('projectManagement.listLabel')}
                  rows={pageRows}
                  renderCard={(project) => <ProjectCardRow project={project} />}
                  selectedId={selectedProject?.id}
                  onSelect={setSelectedProject}
                  isLoading={isLoading}
                  isBusy={isLoading && hasLoaded}
                  emptyLabel={t('projectManagement.table.empty')}
                  canClearFilters={canClearFilters}
                  onClearFilters={clearFilters}
                />
              ) : (
                <DataTable
                  label={t('projectManagement.listLabel')}
                  rows={pageRows}
                  columns={tableColumns}
                  getColumnWidth={columns.getColumnWidth}
                  onResizeColumn={columns.setColumnWidth}
                  onResetColumnWidth={columns.resetColumnWidth}
                  pinnedKeys={PINNED_COLUMNS}
                  filters={filters}
                  onFilterChange={handleFilter}
                  getFilterState={getFilterState}
                  renderCell={(project, key) => <ProjectCell columnKey={key} project={project} />}
                  getCellTitle={getCellTitle}
                  selectedId={selectedProject?.id}
                  onSelect={setSelectedProject}
                  isLoading={isLoading}
                  isBusy={isLoading && hasLoaded}
                  skeletonRows={pageSize}
                  maxCardHeight={maxCardHeight}
                  onCapacityChange={setPageSize}
                  emptyLabel={t('projectManagement.table.empty')}
                  canClearFilters={canClearFilters}
                  onClearFilters={clearFilters}
                  onShowColumns={columns.resetColumns}
                />
              )}

              <TablePagination
                rangeStart={pageStart + 1}
                rangeEnd={pageStart + pageRows.length}
                resultCount={filteredProjects.length}
                rangeOf={t('projectManagement.pagination.rangeOf', { total: filteredProjects.length.toLocaleString(locale) })}
                ofTotal={isFilterActive ? t('projectManagement.pagination.ofTotal', { total: stats.total.toLocaleString(locale) }) : undefined}
                currentPage={currentPage}
                totalPages={totalPages}
                isLoading={isLoading && hasLoaded}
                compact={compactList}
                onPage={goToPage}
              />
            </section>

            {dockPanel && <ProjectDetailPane docked height={fitHeight} {...paneProps} />}
          </motion.div>
        </motion.div>

        {/* Below the docking width: a drawer over the page (portalled to <body>) */}
        {!dockPanel && <ProjectDetailPane {...paneProps} />}
      </MotionConfig>

      {/* Tailwind dialogs */}
      {modal === 'card' && selectedProject && (
        <ProjectCardModal project={selectedProject} onClose={closeModal} onEdit={() => setModal('edit')} />
      )}
      {modal === 'stats' && <ProjectStatsModal projects={allProjects} onClose={closeModal} />}
      {modal === 'screenFail' && selectedProject && <ScreenFailModal project={selectedProject} onClose={closeModal} />}
      <ConfirmDialog
        isOpen={modal === 'status' && Boolean(selectedProject)}
        title={t('projectManagement.confirm.title')}
        message={t('projectManagement.confirm.status', {
          status: nextStatus === 'Aktif' ? t('projectManagement.status.active') : t('projectManagement.status.inactive'),
        })}
        icon={LuToggleRight}
        tone={nextStatus === 'Pasif' ? 'danger' : 'primary'}
        confirmLabel={t('projectManagement.confirm.confirm')}
        cancelLabel={t('projectManagement.confirm.cancel')}
        busy={confirmBusy}
        onConfirm={confirmStatusChange}
        onCancel={closeModal}
      />
      <ConfirmDialog
        isOpen={modal === 'finish' && Boolean(selectedProject)}
        title={t('projectManagement.confirm.title')}
        message={nextFinished
          ? t('projectManagement.confirm.finish', { name: projectName })
          : t('projectManagement.confirm.unfinish', { name: projectName })}
        icon={LuFlag}
        tone="primary"
        confirmLabel={t('projectManagement.confirm.confirm')}
        cancelLabel={t('projectManagement.confirm.cancel')}
        busy={confirmBusy}
        onConfirm={confirmFinish}
        onCancel={closeModal}
      />

      {/* Legacy dialogs (next steps of the redesign) */}
      {modal === 'add' && <AddProjectModal onClose={closeModal} onUpdate={refreshProjects} />}
      {modal === 'edit' && selectedProject && (
        <EditProjectModal project={selectedProject} onClose={closeModal} onUpdate={refreshProjects} />
      )}
      {modal === 'workers' && selectedProject && <WorkersModal project={selectedProject} onClose={closeModal} />}
      {modal === 'visits' && selectedProject && <VisitsModal project={selectedProject} onClose={closeModal} />}
      {modal === 'patients' && selectedProject && <PatientsModal project={selectedProject} onClose={closeModal} />}
      {modal === 'files' && selectedProject && (
        <FileUploadModal
          project={selectedProject}
          onClose={() => {
            closeModal();
            refreshProjects();
          }}
        />
      )}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={COLUMN_KEYS.filter((key) => key !== 'projectStatus').map((key) => ({
          key,
          label: columnLabel(t, key),
          type: PROJECT_COLUMNS[key].type,
          options: translatedOptions(key),
        }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={filteredProjects.length}
      />
    </>
  );
};

export default ProjectsManagementPage;
